import { useEffect, useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { useConfirm } from '../context/ConfirmContext';
import { createExpense, updateExpense } from '../utils/api';
import { getTasks, getProjects } from '../utils/api';
import EmptyState from '../components/common/EmptyState';

const CATEGORIES = ['repair', 'maintenance', 'upgrade', 'supplies', 'labor', 'other'];
const CATEGORY_COLORS = {
  repair: '#ef4444', maintenance: '#6366f1', upgrade: '#10b981',
  supplies: '#f59e0b', labor: '#3b82f6', other: '#94a3b8',
};

const fmt = (date) => new Date(date).toLocaleDateString();
const fmtMoney = (n) => `$${Number(n).toFixed(2)}`;

const blankForm = {
  amount: '',
  date: new Date().toISOString().split('T')[0],
  category: 'repair',
  vendor: '',
  description: '',
  linkedTask: '',
  linkedProject: '',
  notes: '',
};

const Expenses = () => {
  const { expenses, fetchExpenses, removeExpense, loading } = useApp();
  const { confirm } = useConfirm();

  const handleDelete = async (e) => {
    if (await confirm({ title: 'Delete expense', message: `${e.description} — $${Number(e.amount).toFixed(2)}` })) {
      removeExpense(e._id).catch(() => {});
    }
  };
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [catFilter, setCatFilter] = useState('all');

  useEffect(() => {
    fetchExpenses();
    getTasks().then(({ data }) => setTasks(data));
    getProjects().then(({ data }) => setProjects(data));
  }, [fetchExpenses]);

  const total = expenses.reduce((sum, e) => sum + e.amount, 0);

  // Category breakdown
  const byCategory = useMemo(() => {
    const map = {};
    expenses.forEach((e) => {
      map[e.category] = (map[e.category] || 0) + e.amount;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [expenses]);

  const filtered = useMemo(() => {
    let list = catFilter === 'all' ? expenses : expenses.filter((e) => e.category === catFilter);
    list = [...list].sort((a, b) => {
      if (sortField === 'date') {
        return sortDir === 'desc'
          ? new Date(b.date) - new Date(a.date)
          : new Date(a.date) - new Date(b.date);
      }
      if (sortField === 'amount') {
        return sortDir === 'desc' ? b.amount - a.amount : a.amount - b.amount;
      }
      return 0;
    });
    return list;
  }, [expenses, catFilter, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortField(field); setSortDir('desc'); }
  };

  const openNew = () => { setForm(blankForm); setEditId(null); setShowForm(true); };
  const openEdit = (e) => {
    setForm({
      ...blankForm, ...e,
      date: new Date(e.date).toISOString().split('T')[0],
      linkedTask: e.linkedTask?._id || '',
      linkedProject: e.linkedProject?._id || '',
    });
    setEditId(e._id);
    setShowForm(true);
  };

  const handleChange = (ev) => {
    const { name, value } = ev.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form, amount: Number(form.amount),
        linkedTask: form.linkedTask || undefined,
        linkedProject: form.linkedProject || undefined,
      };
      if (editId) await updateExpense(editId, payload);
      else await createExpense(payload);
      await fetchExpenses({ force: true });
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading…</div>;

  const sortIcon = (field) => sortField === field ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '';

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Expenses</h1>
          <p className="page-subtitle">Total: {fmtMoney(total)}</p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ Add Expense</button>
      </div>

      {/* Category breakdown */}
      {byCategory.length > 0 && (
        <div className="expense-breakdown">
          {byCategory.map(([cat, amt]) => (
            <button
              key={cat}
              className={`expense-cat-chip${catFilter === cat ? ' active' : ''}`}
              style={{ '--cat-color': CATEGORY_COLORS[cat] || '#94a3b8' }}
              onClick={() => setCatFilter(catFilter === cat ? 'all' : cat)}
            >
              <span className="expense-cat-dot" />
              <span className="expense-cat-name">{cat}</span>
              <span className="expense-cat-amt">{fmtMoney(amt)}</span>
            </button>
          ))}
          {catFilter !== 'all' && (
            <button className="btn btn-secondary btn-sm" onClick={() => setCatFilter('all')}>
              Clear filter
            </button>
          )}
        </div>
      )}

      {showForm && (
        <div className="expense-form-wrap">
          <form onSubmit={handleSubmit} className="room-form">
            <fieldset className="form-section">
              <legend className="form-section-title">{editId ? 'Edit Expense' : 'New Expense'}</legend>
              <div className="form-row">
                <label>Amount ($) *
                  <input type="number" name="amount" value={form.amount} onChange={handleChange} required min={0} step="0.01" />
                </label>
                <label>Date *
                  <input type="date" name="date" value={form.date} onChange={handleChange} required />
                </label>
                <label>Category *
                  <select name="category" value={form.category} onChange={handleChange}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
              </div>
              <div className="form-row">
                <label>Description *
                  <input name="description" value={form.description} onChange={handleChange} required />
                </label>
                <label>Vendor
                  <input name="vendor" value={form.vendor} onChange={handleChange} />
                </label>
              </div>
              <div className="form-row">
                <label>Linked Task
                  <select name="linkedTask" value={form.linkedTask} onChange={handleChange}>
                    <option value="">— none —</option>
                    {tasks.map((t) => <option key={t._id} value={t._id}>{t.title}</option>)}
                  </select>
                </label>
                <label>Linked Project
                  <select name="linkedProject" value={form.linkedProject} onChange={handleChange}>
                    <option value="">— none —</option>
                    {projects.map((p) => <option key={p._id} value={p._id}>{p.title}</option>)}
                  </select>
                </label>
              </div>
              <label>Notes
                <textarea name="notes" value={form.notes} onChange={handleChange} rows={2} />
              </label>
            </fieldset>
            <div className="form-actions form-actions-bottom">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Save Changes' : 'Add Expense'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {filtered.length === 0 ? (
        catFilter === 'all' ? (
          <EmptyState
            icon="💸"
            title="No expenses logged"
            message="Record what you spend on repairs, supplies, and contractors to track your home's running costs."
            actionLabel="+ Add Expense"
            onAction={openNew}
          />
        ) : (
          <p className="empty-state">No expenses in "{catFilter}" yet.</p>
        )
      ) : (
        <div className="table-scroll"><table className="data-table">
          <thead>
            <tr>
              <th onClick={() => toggleSort('date')} style={{ cursor: 'pointer' }}>Date{sortIcon('date')}</th>
              <th>Description</th>
              <th>Category</th>
              <th>Vendor</th>
              <th>Linked</th>
              <th onClick={() => toggleSort('amount')} style={{ cursor: 'pointer' }}>Amount{sortIcon('amount')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e._id}>
                <td style={{ whiteSpace: 'nowrap' }}>{fmt(e.date)}</td>
                <td>{e.description}</td>
                <td>
                  <span className="badge" style={{
                    background: `${CATEGORY_COLORS[e.category]}22`,
                    color: CATEGORY_COLORS[e.category],
                    border: `1px solid ${CATEGORY_COLORS[e.category]}44`,
                  }}>
                    {e.category}
                  </span>
                </td>
                <td>{e.vendor || '—'}</td>
                <td style={{ fontSize: '0.82rem', color: 'var(--muted)' }}>
                  {e.linkedTask ? `Task: ${e.linkedTask.title}` : ''}
                  {e.linkedProject ? `Project: ${e.linkedProject.title}` : ''}
                  {!e.linkedTask && !e.linkedProject ? '—' : ''}
                </td>
                <td className="expense-amount">{fmtMoney(e.amount)}</td>
                <td className="table-actions">
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(e)}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(e)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5} style={{ textAlign: 'right', fontWeight: 700 }}>
                {catFilter !== 'all' ? `${catFilter} total` : 'Total'}
              </td>
              <td className="expense-amount" style={{ fontWeight: 700 }}>
                {fmtMoney(filtered.reduce((s, e) => s + e.amount, 0))}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table></div>
      )}
    </div>
  );
};

export default Expenses;
